/**
 * Legacy Sendbird v3 helpers.
 *
 * V3-only factories, adapters, and SDK wrappers.
 * Shared factories (ChatUserFactory, ChatAdapter) remain in domain/chat/helpers.js.
 */
/* eslint object-shorthand: 0 */
/* eslint func-names: 0 */
// eslint-disable-file max-len
import moment from 'moment';
import get from 'lodash/get';
import { Record, List, OrderedMap, Set } from 'immutable';

import { makeChannelName as makeChannelNameFactory } from 'sendbird-utils';
import CONST from '../../constants';
import { notify } from 'lib/errorLogger';

export const messagesShortTimeFormat = 'MMM D';
export const messagesLongTimeFormat = 'MMM D YYYY';

export function sbInstance(sendbird) {
  if (sendbird && 'getConnectionState' in sendbird) {
    const state = sendbird.getConnectionState();
    if (state !== 'OPEN') notify.captureEvent(new Error(`Chat is not open WS: ${state}`));
    if (state === 'CLOSED') sendbird.reconnect();
  } else {
    notify.captureEvent(new Error('No Sendbird instance'));
  }
}

export const CounterFactory = new Record({
  flat: new Map(),
  pear: new Map(),
});

export const PushNotificationPayloadFactory = new Record({
  url: '',
  companyId: '',
  name: '',
  unreadMessageCount: 0,
  members: new List(),
  messages: new List(),
});

export const PushNotificationDocPayloadFactory = new Record({
  url: '',
  companyId: '',
  documentId: '',
  name: '',
  unreadMessageCount: 0,
  members: new List(),
  messages: new List(),
});

export const PushNotificationPayloadMemberFactory = new Record({
  userId: '',
  nickname: '',
  profileUrl: '',
});

export const PushNotificationPayloadMessageFactory = new Record({
  messageId: 0,
  message: '',
  createdAt: 0,
  updatedAt: 0,
  sender: PushNotificationPayloadMemberFactory({}),
  data: '',
});

const getUnredMessagesCount = (members, sender) => {
  const res = members.reduce(
    (r, member) => (r === null && member.user_id !== sender.user_id ? member.channel_unread_message_count : r),
    null,
  );
  return res || 0;
};

const extractDocIdFromChanelName = (name) => {
  const parts = name.split('#');
  return parts.length === 5 ? parts[2] : null;
};

export function PushNotificationPayloadMemberAdapter(member) {
  const userId = get(member, 'user_id');
  const nickname = get(member, 'nickname');
  const profileUrl = get(member, 'profile_url');
  return PushNotificationPayloadMemberFactory({ userId, nickname, profileUrl });
}

export function PushNotificationPayloadMessageAdapter(payload, member) {
  const messageId = get(payload, 'message_id');
  const message = get(payload, 'message');
  const createdAt = get(payload, 'created_at');
  const sender = PushNotificationPayloadMemberAdapter(member);
  const data = get(payload, 'data');
  return PushNotificationPayloadMessageFactory({ messageId, message, createdAt, sender, data });
}

export function PushNotificationPayloadAdapter(data) {
  const url = get(data, 'data.channel.channel_url', '');
  const companyId = get(JSON.parse(get(data, 'data.payload.data')), 'companyId');
  const members = new List(get(data, 'data.members', []).map(PushNotificationPayloadMemberAdapter));
  const name = get(data, 'data.channel.name', '');
  const unreadMessageCount = getUnredMessagesCount(get(data, 'data.members'), get(data, 'data.sender'));
  const messages = new Set([
    PushNotificationPayloadMessageAdapter(get(data, 'data.payload'), get(data, 'data.sender')),
  ]);
  const documentId = extractDocIdFromChanelName(name);

  const factory = documentId ? PushNotificationDocPayloadFactory : PushNotificationPayloadFactory;
  return factory({ url, companyId, documentId, members, name, unreadMessageCount, messages });
}

function creatyeMessageFactory() {
  const sf = new Record({
    nickname: '',
    userId: '',
    profileUrl: '',
  });
  const df = new Record({
    companyId: '',
  });
  const defMessage = () => ({
    messageId: 0,
    message: '',
    createdAt: 0,
    updatedAt: 0,
    sender: sf(),
    data: df(),
  });
  const mf = new Record(defMessage());

  return (message) =>
    mf(
      Object.assign(defMessage(), message, {
        sender: sf(message.sender),
        data: df(JSON.parse(message.data || '{}')),
      }),
    );
}

export const MessageFactory = creatyeMessageFactory();

export function MessageAdapter(messages) {
  return messages.reduce((a, v) => a.add(MessageFactory(v)), new Set());
}

export const groupByTodayYesterday = (date) =>
  moment(date).calendar(null, {
    sameDay: '[Today]',
    nextDay: '[got message from future?]',
    nextWeek: '[got message from future?]',
    lastDay: '[Yesterday]',
    lastWeek: messagesShortTimeFormat,
    sameElse: function (now) {
      if (this.isBefore(now)) {
        if (moment().isSame(this, 'year')) {
          return messagesShortTimeFormat;
        }
        return messagesLongTimeFormat;
      }
      return '[got message from future?]';
    },
  });

export function GroupMessages(d) {
  const sorter = (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  const presentGroups = d
    .sort(sorter)
    .groupBy((e) => groupByTodayYesterday(e.createdAt))
    .reverse();
  return OrderedMap(presentGroups).reverse();
}

export const DOCUMENT_CHAT_P2P_TYPE = 'document_p2p';

export function disconnect(sendbird) {
  return new Promise((resolve) => {
    if (sendbird && 'disconnect' in sendbird) {
      sendbird.disconnect(() => resolve());
    } else {
      resolve();
    }
  });
}

export function connect(sendbird, userId, accessToken) {
  return new Promise((resolve, reject) => {
    if (sendbird && 'connect' in sendbird && userId && accessToken) {
      sendbird.connect(userId, accessToken, (user, error) => {
        if (error) reject(error);
        if (user) resolve(user);
      });
    } else {
      reject(new Error('No SendBird'));
    }
  });
}

export function createChannel(sendbird, ...args) {
  return new Promise((resolve, reject) => {
    sendbird.GroupChannel.createChannelWithUserIds(...args, (res, error) => {
      if (error) reject(error);
      if (res) resolve(res);
    });
  });
}

export function loadMessage(messageListQuery, limit = 20) {
  return new Promise((resolve, reject) => {
    messageListQuery.load(limit, true, (res, err) => {
      if (err) reject(err);
      if (res) resolve(res);
    });
  });
}

export function sendMessage(channel, message, data = '') {
  return new Promise((resolve, reject) => {
    channel.sendUserMessage(message, data, '', (res, err) => {
      if (err) reject(err);
      if (res) resolve(res);
    });
  });
}

export function channelList(sendbird, limit = 20) {
  const channelListQuery = sendbird.GroupChannel.createMyGroupChannelListQuery();
  channelListQuery.limit = limit;
  return new Promise((resolve, reject) => {
    channelListQuery.next((res, err) => {
      if (err) reject(err);
      if (res) resolve(res);
    });
  });
}

export function channelExists(sendbird, threadID) {
  const channelListQuery = sendbird.GroupChannel.createMyGroupChannelListQuery();
  channelListQuery.channelNameContainsFilter = threadID;
  channelListQuery.includeEmpty = true;
  return new Promise((resolve, reject) => {
    channelListQuery.next((res, err) => {
      if (err) reject(err);
      if (res) resolve(res);
    });
  });
}

export function promisefy(fun, ...args) {
  return new Promise((resolve, reject) => {
    fun(...args, (res, err) => {
      if (err) reject(err);
      if (res) resolve(res);
    });
  });
}

export function makeChannelName(participants, companyId, documentId) {
  return makeChannelNameFactory(CONST.BUILD_ENV, participants, companyId, documentId);
}

const serializedKeys = {
  message_id: 'messageId',
  last_message: 'lastMessage',
  created_at: 'createdAt',
  updated_at: 'updatedAt',
  user: 'sender',
  user_id: 'userId',
  profile_url: 'profileUrl',
  unread_message_count: 'unreadMessageCount',
  document_id: 'documentId',
  company_id: 'companyId',
  channel_url: 'url',
};

function ChatWorkerThreadFactory(a) {
  return Object.entries(a).reduce(
    (ac, [k, v]) => ({
      ...ac,
      [serializedKeys[k] || k]: Array.isArray(v)
        ? v.map(ChatWorkerThreadFactory)
        : v instanceof Object
          ? ChatWorkerThreadFactory(v)
          : v,
    }),
    {},
  );
}

export function ChatWorkerThreadsAdapter(channels) {
  return channels.map(ChatWorkerThreadFactory);
}
